import { css, StyleDeclarationValue } from 'aphrodite';
import { observer } from 'mobx-react';
import moment, { Moment } from 'moment';
import * as React from 'react';
import { DayModifiers } from 'react-day-picker-old/types/common';
import { toast } from 'sonner';
import { ContentCalendarSuggestionAll, TemplateType } from '../../../../extViewmodels';
import { useErrorMessages, useFullscreenModal } from '../../../../models/hooks/appStateHooks';
import { Topics } from '../../../../models/LocalNotificationTopics';
import { getScheduleTimeOptions } from '../../../../models/UiUtils';
import { CreateGhostCalendarSuggestionViewModel } from '../../../../viewmodels/AdminViewModels';
import { Button } from '../../../../web/components/Button';
import { DayPicker } from '../../../../web/components/DayPicker';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import {
	INotificationServiceComponentProps,
	withNotificationService,
} from '../../../../web/components/LocalNotificationObserver/WithNotificationService';
import { MultiContainerHeader } from '../../../../web/components/MultiContainerHeader';
import { RadioButton } from '../../../../web/components/RadioButton';
import { DefaultSelectBox, ISelectBoxOption } from '../../../../web/components/SelectBox';
import { GhostCalendarLogoGraphic } from '../../../../web/components/svgs/graphics/GhostCalendarLogoGraphic';
import { ExpiryIcon } from '../../../../web/components/svgs/icons/ExpiryIcon';
import { SendNowIcon } from '../../../../web/components/svgs/icons/SendNowIcon';
import { TextInput } from '../../../../web/components/TextInput';
import { baseStyleSheet } from '../../../../web/styles/styles';
import { styleSheet } from './styles';

interface IProps extends INotificationServiceComponentProps<any> {
	className?: string;
	createSuggestion: CreateGhostCalendarSuggestionViewModel;
	styles?: StyleDeclarationValue[];
}

enum CompleteBy {
	Asap,
	Spread,
}

const GhostCalendarSendOptionsBase: React.FC<IProps> = observer(props => {
	const { className, createSuggestion, postNotification, styles = [] } = props;
	const fullscreenModal = useFullscreenModal();
	const errorMessages = useErrorMessages();

	const [showSuccess, setShowSuccess] = React.useState(false);

	// #region start date
	const [startMoment, setStartMoment] = React.useState<Moment>(
		createSuggestion?.startDate ? moment(createSuggestion.startDate) : moment()
	);

	const templateType = createSuggestion?.template?.templateType || TemplateType.Email;
	let expirationWarning: React.ReactNode;

	const timeOptions = React.useMemo(() => {
		const allOptions = getScheduleTimeOptions(0);
		const nowMoment = moment();
		if (startMoment?.isSame(nowMoment, 'day')) {
			const options = allOptions.filter(({ value }) => {
				const thenMoment = moment(nowMoment)
					.set('hours', Math.floor(value / 60))
					.set('minutes', value % 60);
				return thenMoment.isAfter(nowMoment);
			});
			return options;
		}

		return allOptions;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [startMoment]);

	const onTimeOptionSelected = React.useCallback(
		(option: ISelectBoxOption<number>) => {
			if (startMoment) {
				const nextSelectedMoment = moment(startMoment);

				nextSelectedMoment.set('hours', Math.floor(option.value / 60));
				nextSelectedMoment.set('minutes', option.value % 60);
				setStartMoment(nextSelectedMoment);
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[startMoment]
	);

	const validSelectedHour = React.useMemo(() => {
		return (
			(startMoment
				? timeOptions.find(x => x.value === startMoment.get('hours') * 60 + startMoment.get('minutes'))
				: null) || {
				isDefault: true,
				title: 'Select a time',
				value: -1,
			}
		);
	}, [startMoment, timeOptions]);

	React.useEffect(() => {
		const filteredOptions = timeOptions.filter(x => x.value >= 0);

		if (filteredOptions.length > 0 && validSelectedHour.value < 0) {
			// chose the first valid option

			onTimeOptionSelected(timeOptions.find(x => x.value >= 0));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [validSelectedHour, timeOptions]);
	// #endregion

	const [completeByDays, setCompleteByDays] = React.useState<number>(0);
	const onCompleteByDaysChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
		const num = parseInt(e.target.value, 10);
		setCompleteByDays(num);
	};

	const [sendAsap, setSendAsap] = React.useState<boolean>(createSuggestion?.sendAsap);
	const onCompleteByOptionChanged = (option: CompleteBy) => () => setSendAsap(option === CompleteBy.Asap);

	const onDaySelected = (day: Date, _: DayModifiers, e: React.MouseEvent<HTMLElement>) => {
		if (moment(day).isBefore(moment().startOf('day'))) {
			e.stopPropagation();
			e.preventDefault();
			return;
		}
		const nextMoment = moment(day);
		if (startMoment) {
			nextMoment.set('hours', startMoment.get('hours'));
			nextMoment.set('minutes', startMoment.get('minutes'));
		}

		setStartMoment(nextMoment);
	};

	const onScheduleCtaClicked = () => {
		createSuggestion.startDate = startMoment;
		createSuggestion.completeByDays = completeByDays;
		createSuggestion.sendAsap = sendAsap;
		createSuggestion
			?.create()
			?.then(() => {
				setShowSuccess(true);

				postNotification({ topic: Topics.GHOST_CAL_UPDATE });
			})
			.catch(err => {
				errorMessages.pushApiError(err);
			});
	};

	const onClose = () => {
		fullscreenModal.dismissModal();
	};

	const onSave = React.useCallback(() => {
		if (createSuggestion) {
			const originalStartDate = createSuggestion.startDate;
			const originalCompleteByDays = createSuggestion.completeByDays;
			const originalSendAsap = createSuggestion.sendAsap;

			createSuggestion.startDate = startMoment;
			createSuggestion.completeByDays = completeByDays;
			createSuggestion.sendAsap = sendAsap;

			createSuggestion
				.update()
				.then(() => {
					fullscreenModal?.history.goBack();

					toast.success('Suggestion updated');

					postNotification({ topic: Topics.GHOST_CAL_UPDATE });
				})
				.catch(err => {
					errorMessages.pushApiError(err);
					createSuggestion.startDate = originalStartDate;
					createSuggestion.completeByDays = originalCompleteByDays;
					createSuggestion.sendAsap = originalSendAsap;
				});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [createSuggestion, startMoment, completeByDays, sendAsap]);

	const renderSubverticalsSuccess = () => {
		if (!createSuggestion?.subverticals?.length) {
			return 'All';
		}
		if (createSuggestion?.subverticals?.length === 1) {
			return createSuggestion?.subverticals[0] === ContentCalendarSuggestionAll
				? 'All'
				: createSuggestion?.subverticals[0];
		}
		return createSuggestion?.subverticals.length;
	};

	if (showSuccess) {
		return (
			<div>
				<MultiContainerHeader fullscreenHeader='Send Options' />
				<div className={css(styleSheet.successContainer)}>
					<GhostCalendarLogoGraphic />
					<div className={css(styleSheet.successMessage)}>
						This campaign has been successfully added to the ghost calendar.
					</div>
					<span className={css(styleSheet.subText)}>
						{'Subverticals included: '}
						<b>{renderSubverticalsSuccess()}</b>
					</span>
					{createSuggestion?.targets?.length > 0 ? (
						<span className={css(styleSheet.subText)}>
							{'Target(s) selected: '}
							<b>
								{createSuggestion?.targets?.map((x, i) => (
									<span key={i}>
										{createSuggestion?.targets?.length - 1 === i ? `${x.provider}` : `${x.provider}, `}
									</span>
								))}
							</b>
						</span>
					) : null}
					{createSuggestion?.template?.templateType === TemplateType.Email ? (
						<span className={css(styleSheet.subText)}>
							{'Tag(s) selected: '}
							<b>{`${
								createSuggestion?.tags?.length === 1 ? createSuggestion?.tags[0] : createSuggestion?.tags?.length
							}`}</b>
						</span>
					) : null}
					<button className={css(baseStyleSheet.ctaButton, styleSheet.button)} onClick={onClose}>
						<span>Okay</span>
					</button>
				</div>
			</div>
		);
	}

	const formattedExpirationDate = new Date(createSuggestion?.template?.schedule?.expirationDate);
	const disabledDays = formattedExpirationDate
		? [{ after: formattedExpirationDate, before: new Date() }]
		: [{ before: new Date() }];
	if (createSuggestion?.template?.schedule?.expirationDate) {
		expirationWarning = (
			<div className={css(styleSheet.expirationWarning, styleSheet.warningMessage)}>
				<ExpiryIcon fill='#d0021b' />
				<p className={css(styleSheet.expirationWarningText)}>{`This is a time-sensitive campaign. ${
					templateType === TemplateType.Email ? 'Emails' : 'Posts'
				} will not be sent after ${formattedExpirationDate.toLocaleDateString()}.`}</p>{' '}
			</div>
		);
	}

	const isStartAfterExpiration = startMoment?.isAfter(moment(formattedExpirationDate));

	return (
		<div className={`${css(styleSheet.container, ...styles)} ghost-calendar-send-options ${className || ''}`}>
			<MultiContainerHeader fullscreenHeader='Send Options' />
			<div className={css(styleSheet.header)}>
				<SendNowIcon className={css(styleSheet.headerIcon)} />
				<div className={css(styleSheet.headerText)}>
					{
						<p>
							{templateType === TemplateType.SocialMediaPost
								? 'When do you want to schedule this post?'
								: templateType === TemplateType.Blog
									? 'When do you want to schedule this blog post'
									: 'When do you want to send this email?'}
						</p>
					}{' '}
					<p className={css(styleSheet.headerDescription)}>Send it now or choose a future date and time.</p>
				</div>
			</div>
			<div className={css(styleSheet.body)}>
				<DayPicker
					className={css(styleSheet.dayPicker)}
					onDayClick={onDaySelected}
					selectedDays={[startMoment.toDate()]}
					disabledDays={disabledDays}
				/>
			</div>
			<footer className={css(expirationWarning ? styleSheet.footerWithWarning : styleSheet.footer)}>
				{expirationWarning}

				<div className={css(styleSheet.footerInputs)}>
					<div className={css(styleSheet.selectedDayStringRep)}>{startMoment.format(`ddd[,] MMMM Do `)}</div>
					<div className={css(baseStyleSheet.verticalStack, styleSheet.footerInputsInner)}>
						<div className={css(baseStyleSheet.horizontalStack)}>
							<DefaultSelectBox
								className={css(styleSheet.timeDropdown)}
								menuPopupPosition='top'
								onSelectionChanged={onTimeOptionSelected}
								options={timeOptions}
								optionStyles={[styleSheet.timeDropdownOption]}
								placeholder={<span className={css(styleSheet.timeDropdownPlaceholder)}>Select a time...</span>}
								selectedOption={validSelectedHour}
								triggerStyles={[styleSheet.timeDropdownTrigger]}
							/>
							{!createSuggestion?.isEditing && (
								<button
									className={css(baseStyleSheet.ctaButton)}
									disabled={createSuggestion?.isBusy || validSelectedHour.isDefault || isStartAfterExpiration}
									onClick={onScheduleCtaClicked}
								>
									<span>Schedule</span>
								</button>
							)}
							{createSuggestion?.isBusy && <LoadingSpinner type='small' />}
						</div>{' '}
					</div>
				</div>
			</footer>
			{templateType === TemplateType.Email ? (
				<div
					className={`${css(styleSheet.completeBy, ...(styles || []))} edit-send-options-complete-by ${
						className || ''
					}`}
				>
					<RadioButton
						checked={sendAsap}
						className={css(styleSheet.radio)}
						iconClassName={css(styleSheet.radioIcon)}
						id='asap'
						labelClassName={css(styleSheet.radioLabel, styleSheet.radioLabelLeft)}
						name='complete-by'
						onChange={onCompleteByOptionChanged(CompleteBy.Asap)}
					>
						{templateType === TemplateType.Email ? 'Send as quickly as possible' : 'Post now'}
					</RadioButton>
					<RadioButton
						checked={!sendAsap}
						className={css(styleSheet.radio)}
						iconClassName={css(styleSheet.radioIcon)}
						id='spread'
						labelClassName={css(styleSheet.radioLabel)}
						name='complete-by'
						onChange={onCompleteByOptionChanged(CompleteBy.Spread)}
					>
						<div className={css(baseStyleSheet.verticalStack)}>
							<div className={css(baseStyleSheet.horizontalStack)}>
								<span>Spread out over</span>
								<TextInput
									className={css(baseStyleSheet.textField)}
									inputClassName={css(styleSheet.completeByDays)}
									inputId='complete-by-days-input'
									min={1}
									onChange={onCompleteByDaysChanged}
									step={1}
									type='number'
									value={completeByDays}
								/>
								<span className={css(styleSheet.fixedWidth30)}>{`day${completeByDays > 1 ? 's' : ''}`}</span>
							</div>
						</div>
					</RadioButton>
				</div>
			) : null}
			{createSuggestion?.isEditing && (
				<Button
					className={css(styleSheet.button)}
					type='button'
					label='Okay'
					onClick={onSave}
					disabled={createSuggestion?.isBusy || isStartAfterExpiration}
				/>
			)}
		</div>
	);
});
export const GhostCalendarSendOptions = withNotificationService(GhostCalendarSendOptionsBase);
